/* ==========================================================================
   Sticky Add to Cart — sticky-add-to-cart.js
   Reformation Theme v9.0.0 / Shopify Online Store 2.0
   Vanilla ES6. No jQuery. No dependencies.
   ========================================================================== */

(() => {
  'use strict';

  /* ------------------------------------------------------------------------
     1. CONFIG — adjust these selectors to match Reformation's exact markup
     if the defaults below don't find your theme's main product form.
     ------------------------------------------------------------------------ */
  const CONFIG = {
    // The theme's real <form action="/cart/add">. Add your own selector
    // first in this comma list if you know it (fastest / most reliable).
    mainFormSelector:
      '[data-type="add-to-cart-form"] form, product-form form, form[action*="/cart/add"]',
    // Wrapper around the form used to decide when the bar should appear.
    // Falls back to the form itself if none of these match.
    mainFormContainerSelector:
      '.product-form, .product__info-container, [data-product-form-container], .product-information',
    footerSelector: 'footer, .footer, #footer, .site-footer',
    cartDrawerSelector: 'cart-drawer, #cart-drawer, .cart-drawer',
    cartRefreshEventNames: ['cart:refresh', 'cart:updated', 'cart:build'],
    addToCartEndpoint: '/cart/add.js',
    cartJsonEndpoint: '/cart.js',
    checkoutUrl: '/checkout',
    successStateDurationMs: 1600,

    /* --------------------------------------------------------------------
       SHOPFLO — fill these in once you confirm your integration mode.
       See notes at the bottom of section 8/9 for how each option is used.
       -------------------------------------------------------------------- */
    shopflo: {
  mode: 'url',

  cartUrl: '/cart',
  checkoutUrl: '/checkout',

  tokenEndpoint: '/apps/shopflo/session-checkout-url',
},
  };

  /* ------------------------------------------------------------------------
     2. Bootstrap — bail early if the sticky bar isn't on this page
     ------------------------------------------------------------------------ */
  const bar = document.querySelector('[data-sticky-atc]');
  if (!bar) return;

  const productDataEl = document.getElementById('StickyATC-ProductJSON');
  if (!productDataEl) return;

  /** @type {Object} Full Shopify product object (product.json shape) */
  const product = JSON.parse(productDataEl.textContent);

  // DOM references inside the sticky bar
  const els = {
    image: bar.querySelector('[data-sticky-atc-image]'),
    title: bar.querySelector('[data-sticky-atc-title]'),
    priceSale: bar.querySelector('[data-sticky-atc-price-sale]'),
    priceCompare: bar.querySelector('[data-sticky-atc-price-compare]'),
    selects: Array.from(bar.querySelectorAll('[data-sticky-atc-select]')),
    qtyInput: bar.querySelector('[data-sticky-atc-qty-input]'),
    qtyMinus: bar.querySelector('[data-sticky-atc-qty-minus]'),
    qtyPlus: bar.querySelector('[data-sticky-atc-qty-plus]'),
    addBtn: bar.querySelector('[data-sticky-atc-add]'),
    addBtnText: bar.querySelector('[data-sticky-atc-add-text]'),
    addBtnSpinner: bar.querySelector('[data-sticky-atc-add-spinner]'),
    buyBtn: bar.querySelector('[data-sticky-atc-buy]'),
    status: bar.querySelector('[data-sticky-atc-status]'),
  };

  const moneyFormat = bar.getAttribute('data-money-format') || '${{amount}}';

  // The theme's real product form (may legitimately be null if not found —
  // sticky bar still works standalone, it just won't sync back to it).
  const mainForm = document.querySelector(CONFIG.mainFormSelector);
  const mainFormContainer =
    (mainForm && mainForm.closest(CONFIG.mainFormContainerSelector)) || mainForm;
  const footer = document.querySelector(CONFIG.footerSelector);

  let currentVariant =
    product.selected_or_first_available_variant ||
    product.variants.find((v) => v.available) ||
    product.variants[0];

  let isSyncingFromMain = false;
  let isSyncingFromSticky = false;
  let successTimer = null;

  /* ------------------------------------------------------------------------
     3. Money formatting — mirrors Shopify's {{ '{{amount}}' }} money_format
     ------------------------------------------------------------------------ */
  function formatMoney(cents) {
    const value = (cents / 100).toFixed(2);
    const [dollars, decimals] = value.split('.');
    const withCommas = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, `${withCommas}.${decimals}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${withCommas},${decimals}`);
  }

  /* ------------------------------------------------------------------------
     4. Variant resolution
     ------------------------------------------------------------------------ */
  function getSelectedOptionValues() {
    return els.selects.map((select) => select.value);
  }

  function findVariantByOptions(optionValues) {
    return product.variants.find((variant) =>
      variant.options.every((value, index) => value === optionValues[index])
    );
  }

  function findVariantById(id) {
    return product.variants.find((variant) => String(variant.id) === String(id));
  }

  /* ------------------------------------------------------------------------
     5. Render sticky bar UI for a given variant
     ------------------------------------------------------------------------ */
  function renderVariant(variant) {
    if (!variant) return;
    currentVariant = variant;

    // Image
    const image = variant.featured_image
      ? variant.featured_image.src
      : product.featured_image
      ? product.featured_image
      : null;
    if (els.image && image) {
      els.image.src = image.startsWith('//') ? `https:${image}` : image;
      els.image.alt = variant.featured_image ? variant.featured_image.alt || product.title : product.title;
    }

    // Price
    if (els.priceSale) els.priceSale.textContent = formatMoney(variant.price);
    if (els.priceCompare) {
      const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      els.priceCompare.textContent = hasCompare ? formatMoney(variant.compare_at_price) : '';
      els.priceCompare.hidden = !hasCompare;
    }

    // Availability
    setAvailability(variant.available);

    // Keep selects in sync with the resolved variant (covers programmatic changes)
    els.selects.forEach((select, index) => {
      if (variant.options[index] !== undefined && select.value !== variant.options[index]) {
        select.value = variant.options[index];
      }
    });
  }

  function setAvailability(available) {
    [els.addBtn, els.buyBtn].forEach((btn) => {
      if (!btn) return;
      btn.disabled = !available;
      btn.setAttribute('aria-disabled', String(!available));
    });
    if (els.addBtnText) {
      els.addBtnText.textContent = available ? 'Add to Bag' : 'Sold Out';
    }
    if (els.buyBtn) {
      els.buyBtn.textContent = available ? 'Buy Now' : 'Sold Out';
    }
  }

  /* ------------------------------------------------------------------------
     6. Two-way sync: sticky bar <-> main product form
     ------------------------------------------------------------------------ */

  // 6a. Sticky bar select changed -> resolve variant -> push to main form
  function handleStickySelectChange() {
    if (isSyncingFromMain) return;
    const variant = findVariantByOptions(getSelectedOptionValues());
    if (!variant) return;
    renderVariant(variant);
    pushVariantToMainForm(variant);
  }

  els.selects.forEach((select) => {
    select.addEventListener('change', handleStickySelectChange);
  });

  function pushVariantToMainForm(variant) {
    if (!mainForm) return;
    isSyncingFromSticky = true;

    const idInput = mainForm.querySelector('input[name="id"], select[name="id"]');
    if (idInput && String(idInput.value) !== String(variant.id)) {
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Best-effort: mirror option selections into the main form's own
    // inputs (radio/select) so its visible UI updates too, if present.
    variant.options.forEach((value, index) => {
      const optionName = product.options[index];
      const field = mainForm.querySelector(
        `[name="options[${optionName}]"], [data-option-index="${index}"]`
      );
      if (!field) return;
      if (field.tagName === 'SELECT') {
        field.value = value;
        field.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        const radio = mainForm.querySelector(
          `input[name="options[${optionName}]"][value="${CSS.escape(value)}"]`
        );
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });

    isSyncingFromSticky = false;
  }

  // 6b. Main form changed (any input, delegated) -> read resulting id -> sync sticky
  if (mainForm) {
    mainForm.addEventListener('change', () => {
      if (isSyncingFromSticky) return;
      // Give the theme's own JS a tick to finish updating its hidden id input.
      window.setTimeout(() => {
        const idInput = mainForm.querySelector('input[name="id"], select[name="id"]');
        if (!idInput) return;
        const variant = findVariantById(idInput.value);
        if (!variant || variant.id === currentVariant.id) return;
        isSyncingFromMain = true;
        renderVariant(variant);
        isSyncingFromMain = false;
      }, 50);
    });
  }

  /* ------------------------------------------------------------------------
     7. Quantity stepper
     ------------------------------------------------------------------------ */
  function getQuantity() {
    const value = parseInt(els.qtyInput ? els.qtyInput.value : '1', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  // Some themes set the quantity text color via an inline style (often
  // with !important) to hide native spinner artifacts. Inline !important
  // beats even our external CSS !important, so we reassert visibility
  // from JS itself — as the last script to touch the element, we win.
  function forceQtyInputVisible() {
    if (!els.qtyInput) return;
    els.qtyInput.style.setProperty('color', '#1a1a1a', 'important');
    els.qtyInput.style.setProperty('-webkit-text-fill-color', '#1a1a1a', 'important');
    els.qtyInput.style.setProperty('caret-color', '#1a1a1a', 'important');
    els.qtyInput.style.setProperty('background-color', '#ffffff', 'important');
    els.qtyInput.style.setProperty('opacity', '1', 'important');
    els.qtyInput.style.setProperty('visibility', 'visible', 'important');
  }

  function setQuantity(value) {
    if (!els.qtyInput) return;
    const clamped = Math.max(1, value);
    els.qtyInput.value = String(clamped);
    forceQtyInputVisible();
  }

  if (els.qtyMinus) {
    els.qtyMinus.addEventListener('click', () => {
      setQuantity(getQuantity() - 1);
    });
  }

  if (els.qtyPlus) {
    els.qtyPlus.addEventListener('click', () => {
      setQuantity(getQuantity() + 1);
    });
  }

  if (els.qtyInput) {
    // Quantity is type="text" (not type="number") so the theme's own
    // global number-input scripts can't auto-adopt it — so we do our
    // own digit-only sanitization here instead.
    els.qtyInput.addEventListener('input', () => {
      const digitsOnly = els.qtyInput.value.replace(/[^0-9]/g, '');
      if (digitsOnly !== els.qtyInput.value) els.qtyInput.value = digitsOnly;
      forceQtyInputVisible();
    });
    els.qtyInput.addEventListener('blur', () => {
      if (getQuantity() < 1) els.qtyInput.value = '1';
      forceQtyInputVisible();
    });
    els.qtyInput.addEventListener('focus', forceQtyInputVisible);

    // Initial paint — guards against the theme's own scripts running
    // after ours and re-hiding the value before the first render call.
    forceQtyInputVisible();
    // Re-assert once more on the next frame, after any theme scripts
    // that patch inputs on DOMContentLoaded/load have had a chance to run.
    window.requestAnimationFrame(forceQtyInputVisible);
  }

  /* ------------------------------------------------------------------------
     8. Add to Cart (AJAX Cart API)
     ------------------------------------------------------------------------ */
  function collectLineItemProperties() {
    if (!mainForm) return undefined;
    const inputs = mainForm.querySelectorAll('[name^="properties["]');
    if (!inputs.length) return undefined;
    const properties = {};
    inputs.forEach((input) => {
      const match = input.name.match(/properties\[(.+?)\]/);
      if (match) properties[match[1]] = input.value;
    });
    return properties;
  }

  function collectSellingPlan() {
    if (!mainForm) return undefined;
    const input = mainForm.querySelector('[name="selling_plan"]');
    return input && input.value ? input.value : undefined;
  }

  function setAddButtonLoading(isLoading) {
    if (!els.addBtn) return;
    els.addBtn.disabled = isLoading || !currentVariant.available;
    if (els.addBtnSpinner) els.addBtnSpinner.hidden = !isLoading;
    if (isLoading && els.addBtnText) els.addBtnText.textContent = 'Adding…';
  }

  function setAddButtonSuccess() {
    if (!els.addBtn || !els.addBtnText) return;
    els.addBtn.classList.remove('is-error');
    els.addBtn.classList.add('is-success');
    els.addBtnText.textContent = 'Added!';
    announce('Item added to your cart.');
    clearTimeout(successTimer);
    successTimer = window.setTimeout(resetAddButton, CONFIG.successStateDurationMs);
  }

  function setAddButtonError(message) {
    if (!els.addBtn || !els.addBtnText) return;
    els.addBtn.classList.remove('is-success');
    els.addBtn.classList.add('is-error');
    els.addBtnText.textContent = 'Try Again';
    announce(message || 'Something went wrong. Please try again.');
    clearTimeout(successTimer);
    successTimer = window.setTimeout(resetAddButton, CONFIG.successStateDurationMs);
  }

  function resetAddButton() {
    if (!els.addBtn || !els.addBtnText) return;
    els.addBtn.classList.remove('is-success', 'is-error');
    els.addBtnText.textContent = currentVariant.available ? 'Add to Bag' : 'Sold Out';
  }

  function announce(message) {
    if (els.status) els.status.textContent = message;
  }

  /* ---- Shopflo redirect helpers -----------------------------------------
     goToShopfloCart()     -> called after a successful Add to Cart
     goToShopfloCheckout() -> called from Buy Now
     Both no-op (fall back to native Shopify behavior) until
     CONFIG.shopflo.mode is set to 'url' or 'sdk'.
     ------------------------------------------------------------------------ */
  async function goToShopfloCart() {
    const { mode, cartUrl } = CONFIG.shopflo;
    if (mode === 'url') {
      window.location.href = cartUrl;
      return true;
    }
    // 'sdk' mode intentionally does not redirect on Add to Cart — Shopflo's
    // SDK flow is normally only invoked at checkout time. If your setup
    // needs an SDK-driven cart drawer instead, tell me and I'll wire it in.
    return false;
  }

  async function goToShopfloCheckout() {
    const { mode, checkoutUrl, tokenEndpoint } = CONFIG.shopflo;

    if (mode === 'url') {
      window.location.href = checkoutUrl;
      return true;
    }

    if (mode === 'sdk') {
      if (typeof window.Shopflo === 'undefined') {
        console.error('[Sticky ATC] Shopflo SDK not found on window.');
        return false;
      }
      try {
        const sessionId = window.Shopflo.getSessionId();
        const response = await fetch(tokenEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
        });
        const data = await response.json();
        if (!response.ok || !data.checkout_url) {
          throw new Error(data.message || 'Unable to get Shopflo checkout URL.');
        }
        window.Shopflo.openFloCheckout(data.checkout_url);
        return true;
      } catch (error) {
        console.error('[Sticky ATC] Shopflo checkout failed:', error);
        return false;
      }
    }

    return false;
  }

  async function addToCart() {
    if (!currentVariant.available || (els.addBtn && els.addBtn.disabled)) return;

    setAddButtonLoading(true);

    const item = {
      id: currentVariant.id,
      quantity: getQuantity(),
    };
    const properties = collectLineItemProperties();
    const sellingPlan = collectSellingPlan();
    if (properties) item.properties = properties;
    if (sellingPlan) item.selling_plan = sellingPlan;

    try {
      const response = await fetch(CONFIG.addToCartEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || 'Unable to add item to cart.');
      }
      // await refreshCart();
      setAddButtonSuccess();

      handleFloCartBtn();
      // const redirected = await goToShopfloCart();
      // if (!redirected) {
      //   // Native behavior unchanged: cart drawer/refresh already handled above.
      // }
    } catch (error) {
      console.error('[Sticky ATC] Add to cart failed:', error);
      setAddButtonError(error.message);
    } finally {
      setAddButtonLoading(false);
    }
  }

  async function refreshCart() {
    const response = await fetch(CONFIG.cartJsonEndpoint);
    const cart = await response.json();

    // Notify the rest of the theme so its cart drawer/icon count can update.
    CONFIG.cartRefreshEventNames.forEach((eventName) => {
      document.dispatchEvent(new CustomEvent(eventName, { detail: { cart }, bubbles: true }));
    });

    // Best-effort: directly trigger a known cart drawer custom element if present.
    const drawer = document.querySelector(CONFIG.cartDrawerSelector);
    if (drawer) {
      if (typeof drawer.renderContents === 'function') {
        drawer.renderContents(cart);
      } else if (typeof drawer.open === 'function') {
        drawer.open();
      } else {
        drawer.classList.add('is-open', 'active');
        drawer.setAttribute('open', '');
      }
    }

    return cart;
  }

  if (els.addBtn) {
    els.addBtn.addEventListener('click', addToCart);
  }

  /* ------------------------------------------------------------------------
     9. Buy Now (direct checkout redirect)
     ------------------------------------------------------------------------ */
  async function buyNow() {
    if (!currentVariant.available || (els.buyBtn && els.buyBtn.disabled)) return;

    els.buyBtn.disabled = true;
    const originalLabel = els.buyBtn.textContent;
    els.buyBtn.textContent = 'Redirecting…';

    const item = { id: currentVariant.id, quantity: getQuantity() };
    const sellingPlan = collectSellingPlan();
    if (sellingPlan) item.selling_plan = sellingPlan;

    try {
      const emptyCart = await fetch('/cart/clear.js', { method: 'POST' });
      const response = await fetch(CONFIG.addToCartEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.description || 'Unable to start checkout.');
      }

      const redirected = await goToShopfloCheckout();
      if (!redirected) {
        window.location.href = CONFIG.checkoutUrl;
      }
    } catch (error) {
      console.error('[Sticky ATC] Buy now failed:', error);
      announce(error.message);
      els.buyBtn.disabled = false;
      els.buyBtn.textContent = originalLabel;
    }
  }

  if (els.buyBtn) {
    els.buyBtn.addEventListener('click', buyNow);
  }

  /* ------------------------------------------------------------------------
     10. Visibility engine — IntersectionObserver (no scroll listeners)
     ------------------------------------------------------------------------ */
  let mainFormOutOfView = false;
  let footerInView = false;

  function updateBarVisibility() {
    const shouldShow = mainFormOutOfView && !footerInView;
    bar.classList.toggle('is-visible', shouldShow);
    bar.setAttribute('aria-hidden', String(!shouldShow));
  }

  if (mainFormContainer && 'IntersectionObserver' in window) {
    const formObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        mainFormOutOfView = !entry.isIntersecting;
        updateBarVisibility();
      },
      { threshold: 0 }
    );
    formObserver.observe(mainFormContainer);
  } else {
    // No form found or no IntersectionObserver support: never show the bar
    // rather than risk it being permanently stuck on screen.
    mainFormOutOfView = false;
  }

  if (footer && 'IntersectionObserver' in window) {
    const footerObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        footerInView = entry.isIntersecting;
        updateBarVisibility();
      },
      { threshold: 0 }
    );
    footerObserver.observe(footer);
  }

  /* ------------------------------------------------------------------------
     11. Initial render
     ------------------------------------------------------------------------ */
  renderVariant(currentVariant);
})();